import React from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../api/apiClient';
import AuditFiltersBar from '../AuditFiltersBar';
import AuditTable, { statusPill, formatTs, monoShort, pre } from '../AuditTable';
import InfoBox from '../InfoBox';
import useAuditList from '../useAuditList';

export default function CypherExecutionsTab({ onOpenTrace }) {
  const { t } = useTranslation();
  const { rows, loading, filters, setFilters, offset, limit, next, prev } =
    useAuditList(apiService.listAuditCypher);

  const columns = [
    { key: 'timestamp', header: t('traceability:columns.timestamp'), render: (r) => formatTs(r.timestamp) },
    { key: 'status', header: t('traceability:columns.status'), render: (r) => statusPill(r.status) },
    { key: 'cypher', header: t('traceability:columns.cypher'),
      render: (r) => pre(r.cypher, 300) },
    { key: 'rows_returned', header: t('traceability:columns.rows'),
      render: (r) => <RowsCell row={r} /> },
    { key: 'duration_ms', header: t('traceability:columns.duration') },
    { key: 'trace_id', header: t('traceability:columns.traceId'),
      render: (r) => <button onClick={(e) => { e.stopPropagation(); onOpenTrace(r.trace_id); }}
                             className="hover:text-accent-text">{monoShort(r.trace_id, 10)}</button> },
  ];

  return (
    <div>
      <InfoBox tabKey="cypher" />
      <AuditFiltersBar value={filters} onChange={setFilters} />
      <AuditTable rows={rows} columns={columns} loading={loading}
                  limit={limit} offset={offset} onNext={next} onPrev={prev}
                  onRowClick={(r) => onOpenTrace(r.trace_id)} />
    </div>
  );
}

// Renders the rows_returned count with an inline collapsed details showing
// a slim sample (up to N rows × capped cells). Default collapsed — the
// sample is only fetched from the row data on expand, so there's no extra
// backend call. If the backend didn't store a sample (old events) the
// expander is hidden.
export function RowsCell({ row }) {
  const n = row?.rows_returned;
  const rawSample = row?.rows_sample_json;
  let parsed = null;
  if (rawSample) {
    try { parsed = typeof rawSample === 'string' ? JSON.parse(rawSample) : rawSample; }
    catch (_) { parsed = null; }
  }
  const hasSample = Array.isArray(parsed) && parsed.length > 0;
  if (!hasSample) return <span>{n ?? '—'}</span>;
  return (
    <details onClick={(e) => e.stopPropagation()} className="text-xs">
      <summary className="cursor-pointer text-ink-secondary hover:text-accent-text list-none">
        <span className="font-medium text-ink">{n ?? parsed.length}</span>
        <span className="text-ink-muted"> · ver muestra ({parsed.length})</span>
      </summary>
      <SampleTable rows={parsed} />
    </details>
  );
}

export function SampleTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  // Columns = union of top-level keys across sampled rows (stable order
  // based on first row, then extras at the end).
  const seen = new Set();
  const cols = [];
  for (const r of rows) {
    if (r && typeof r === 'object') {
      for (const k of Object.keys(r)) {
        if (!seen.has(k)) { seen.add(k); cols.push(k); }
      }
    }
  }
  const cap = (s, n = 80) => {
    if (s === null || s === undefined) return '—';
    const str = typeof s === 'string' ? s : JSON.stringify(s);
    return str.length > n ? str.slice(0, n - 1) + '…' : str;
  };
  return (
    <div className="mt-2 overflow-x-auto rounded border border-border-subtle bg-surface-raised/40">
      <table className="min-w-full text-[11px]">
        <thead className="bg-surface-raised/80">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left px-2 py-1 font-mono text-ink-muted">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border-subtle/60">
              {cols.map((c) => (
                <td key={c} className="px-2 py-1 align-top text-ink-secondary font-mono whitespace-pre-wrap break-all">
                  {cap(r?.[c], 100)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-2 py-1 text-label text-ink-muted border-t border-border-subtle">
        muestra truncada · auditoría gravada en <code>rows_sample_hash</code>
      </div>
    </div>
  );
}
